import React, { useEffect } from "react";
import { useAnalytics } from "../../contexts/AnalyticsContext";

function CompareModal({ isOpen, onClose, comparison }) {
  const { trackClick } = useAnalytics();

  useEffect(() => {
    if (isOpen && comparison) {
      trackClick('similarity_check', {
        game_a_name: comparison.game_a_name,
        game_b_name: comparison.game_b_name,
        source: 'compare_modal',
      });
    }
  }, [isOpen, comparison, trackClick]);

  if (!isOpen || !comparison) return null;

  const { game_a_name, game_b_name, overlaps, reason_summary } = comparison;

  // Normalize similarity score from different API response formats
  // Prioritize final_score so the Compare Modal matches the Curate sidebar tile.
  const getSimilarityScore = (comparisonData) => {
    if (comparisonData.final_score !== undefined && comparisonData.final_score !== null) {
      return comparisonData.final_score;
    }
    if (comparisonData.similarity_score !== undefined && comparisonData.similarity_score !== null) {
      return comparisonData.similarity_score;
    }
    if (comparisonData.embedding_similarity !== undefined && comparisonData.embedding_similarity !== null) {
      return comparisonData.embedding_similarity;
    }
    if (comparisonData.meta_score !== undefined && comparisonData.meta_score !== null) {
      return comparisonData.meta_score;
    }
    return null;
  };

  const similarityScore = getSimilarityScore(comparison);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content compare-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "600px",
          maxHeight: "90dvh",
          overflowY: "auto",
          padding: "24px",
        }}
      >
        <div className="modal-header" style={{ marginBottom: "20px" }}>
          <h2 style={{ margin: 0, color: "var(--text-primary, #333)" }}>
            Compare Games
          </h2>
          <button
            className="modal-close"
            onClick={onClose}
            style={{
              position: "absolute",
              top: "16px",
              right: "16px",
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "var(--text-secondary, #666)",
            }}
          >
            ×
          </button>
        </div>

        <div className="compare-content">
          {/* Game Names */}
          <div
            className="compare-games-row"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "16px",
              marginBottom: "24px",
              padding: "16px",
              border: "1px solid var(--border-color, #e0e0e0)",
              borderRadius: "8px",
            }}
          >
            <div
              className="compare-game-name"
              style={{
                flex: 1,
                textAlign: "center",
                fontWeight: "600",
                fontSize: "1.1rem",
                color: "var(--text-primary, #333)",
              }}
            >
              {game_a_name}
            </div>
            <div
              className="compare-vs"
              style={{
                fontWeight: "700",
                fontSize: "1.2rem",
                color: "var(--primary-color, #1976d2)",
                padding: "8px 16px",
                backgroundColor: "var(--bg-primary, #fff)",
                borderRadius: "20px",
                border: "2px solid var(--primary-color, #1976d2)",
              }}
            >
              VS
            </div>
            <div
              className="compare-game-name"
              style={{
                flex: 1,
                textAlign: "center",
                fontWeight: "600",
                fontSize: "1.1rem",
                color: "var(--text-primary, #333)",
              }}
            >
              {game_b_name}
            </div>
          </div>

          {/* Similarity Score */}
          {similarityScore !== null && (
            <div
              className="compare-score-section"
              style={{
                textAlign: "center",
                marginBottom: "24px",
                padding: "20px",
                backgroundColor: "var(--primary-color, #1976d2)",
                borderRadius: "8px",
                color: "#fff",
              }}
            >
              <div style={{ fontSize: "0.9rem", opacity: 0.9, marginBottom: "8px" }}>
                Similarity Score
              </div>
              <div style={{ fontSize: "2.5rem", fontWeight: "700" }}>
                {(similarityScore * 100).toFixed(1)}%
              </div>
            </div>
          )}

          {/* Reason Summary */}
          {reason_summary && (
            <div
              className="compare-summary-section"
              style={{
                marginBottom: "24px",
                padding: "16px",
                backgroundColor: "var(--bg-secondary, #f5f5f5)",
                borderRadius: "8px",
                borderLeft: "4px solid var(--primary-color, #1976d2)",
              }}
            >
              <h4
                style={{
                  margin: "0 0 12px 0",
                  color: "var(--text-primary, #333)",
                  fontSize: "1rem",
                }}
              >
                Summary
              </h4>
              <p
                style={{
                  margin: 0,
                  color: "var(--text-secondary, #666)",
                  lineHeight: "1.6",
                }}
              >
                {reason_summary}
              </p>
            </div>
          )}

          {/* Shared Features */}
          {overlaps && Object.keys(overlaps).length > 0 && (
            <div className="compare-features-section">
              <h4
                style={{
                  margin: "0 0 16px 0",
                  color: "var(--text-primary, #333)",
                  fontSize: "1rem",
                }}
              >
                Shared Features
              </h4>
              {Object.entries(overlaps)
                .filter(([featureType]) => featureType !== "publishers")
                .map(
                  ([featureType, features]) =>
                    features.length > 0 && (
                      <div
                        key={featureType}
                        className="feature-group"
                        style={{
                          marginBottom: "16px",
                          padding: "12px",
                          backgroundColor: "var(--bg-primary, #fff)",
                          borderRadius: "6px",
                          border: "1px solid var(--border-color, #e0e0e0)",
                        }}
                      >
                        <h5
                          style={{
                            margin: "0 0 8px 0",
                            color: "var(--text-secondary, #666)",
                            fontSize: "0.85rem",
                            textTransform: "capitalize",
                          }}
                        >
                          {featureType}
                        </h5>
                        <div
                          className="feature-list"
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px",
                          }}
                        >
                          {features.map((feature, index) => (
                            <span
                              key={index}
                              className="feature-tag"
                              style={{
                                padding: "4px 10px",
                                backgroundColor: "var(--bg-tertiary, #e0e0e0)",
                                borderRadius: "12px",
                                fontSize: "0.85rem",
                                color: "var(--text-primary, #333)",
                                border: "1px solid var(--border-color, #e0e0e0)",
                              }}
                            >
                              {typeof feature === "string"
                                ? feature
                                : feature.value || feature.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )
                )}
            </div>
          )}
        </div>

        {/* Close Button */}
        <div style={{ marginTop: "24px", textAlign: "center" }}>
          <button
            onClick={onClose}
            style={{
              padding: "12px 32px",
              backgroundColor: "var(--primary-color, #1976d2)",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: "1rem",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = "var(--primary-color-dark, #1565c0)";
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = "var(--primary-color, #1976d2)";
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default CompareModal;
